// Rows under your own rides and requests on Profile. Same features as
// RideMatching.swift and RidePassengersView.swift on iOS.
//
//  • Your ride: "N people need this ride" → Invite each one
//    (invites/{rideId}/{requestId} = { from, to, createdAt }; the
//    onInviteCreated function sends the push), and "N passengers".
//  • Your request: "N rides match your request" → Book one, and the drivers
//    who accepted it.
//
// Matching is requestMatchesRide from places.js: both ends the same place
// (USAFA and its gates count as one, either way round), same day, within 3 hours.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, get, set, onValue, serverTimestamp } from 'firebase/database';
import { departure, displayPlace, requestMatchesRide } from '../places';
import { bookRide, fetchContact } from '../contacts';
import { SafetyMenu, showToast } from '../Safety/Safety';
import './Matching.css';

const MT = 'America/Denver';
const whenFmt = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const when = post => { const d = departure(post); return d ? whenFmt.format(d).replace(':00', '') : ''; };
const seatsOf = post => parseInt(post.seats, 10) || 0;

/** Upcoming posts with seats left, from a rideReserve / rideRequest snapshot value.
 *  Requests whose rider already booked a ride for them (`bookedRide`) are left out. */
export function openPosts(value) {
  const now = Date.now();
  return Object.entries(value || {})
    .map(([id, v]) => ({ id, ...v, _dep: departure(v) }))
    .filter(p => p._dep && p._dep.getTime() > now && seatsOf(p) > 0 && p.userID && !p.bookedRide);
}

/** users/{uid}/name for a handful of people, loaded as needed. */
function useNames(uids) {
  const [names, setNames] = useState({});
  const key = [...new Set(uids)].sort().join(',');
  useEffect(() => {
    if (!key) return;
    let live = true;
    const db = getDatabase();
    Promise.all(key.split(',').map(uid =>
      get(ref(db, `users/${uid}/name`)).then(s => [uid, s.val() || 'Falcon Ride user']).catch(() => [uid, 'Falcon Ride user'])
    )).then(pairs => { if (live) setNames(Object.fromEntries(pairs)); });
    return () => { live = false; };
  }, [key]);
  return names;
}

function Modal({ title, sub, onClose, children }) {
  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="match-overlay" onClick={onClose}>
      <div className="match-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="match-dialog-head">
          <div>
            <h2>{title}</h2>
            {sub && <p>{sub}</p>}
          </div>
          <button className="match-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="match-list">{children}</div>
      </div>
    </div>,
    document.body
  );
}

/* ── Your ride: people who need it ─────────────────────────────── */

function NeedThisRide({ ride, requests, open, setOpen }) {
  const [invited, setInvited] = useState({});
  const [sending, setSending] = useState('');
  const names = useNames(open ? requests.map(r => r.userID) : []);

  // Which requests you've already invited (readable only by the ride's owner).
  useEffect(() => {
    if (!open) return;
    return onValue(ref(getDatabase(), `invites/${ride.id}`), s => setInvited(s.val() || {}), () => {});
  }, [open, ride.id]);

  const invite = async (req) => {
    setSending(req.id);
    try {
      await set(ref(getDatabase(), `invites/${ride.id}/${req.id}`), {
        from: getAuth().currentUser.uid, to: req.userID, createdAt: serverTimestamp(),
      });
      showToast(`Invited ${(names[req.userID] || '').split(' ')[0] || 'them'}`);
    } catch (e) {
      alert(`Couldn't send the invite. ${e.message}`);
    }
    setSending('');
  };

  if (requests.length === 0) return null;
  return (
    <>
      <button className="match-row need" onClick={() => setOpen(true)}>
        <span>👥 {requests.length} {requests.length === 1 ? 'person needs' : 'people need'} this ride</span>
        <span className="match-row-cta">Invite ›</span>
      </button>
      {open && (
        <Modal
          title={`${requests.length} ${requests.length === 1 ? 'person needs' : 'people need'} this ride`}
          sub="They asked for a ride on the same route and day. Invite them and they'll get a notification."
          onClose={() => setOpen(false)}>
          {requests.map(r => (
            <div key={r.id} className="match-item">
              <div className="match-item-main">
                <div className="match-item-name">{names[r.userID] || '…'}</div>
                <div className="match-item-sub">
                  {displayPlace(r.fromLocation)} → {displayPlace(r.toLocation)} · {when(r)} · {seatsOf(r)} seat{seatsOf(r) !== 1 ? 's' : ''}
                </div>
              </div>
              {invited[r.id]
                ? <span className="match-done">Invited ✓</span>
                : <button className="match-btn" disabled={sending === r.id} onClick={() => invite(r)}>
                    {sending === r.id ? 'Inviting…' : 'Invite'}
                  </button>}
            </div>
          ))}
        </Modal>
      )}
    </>
  );
}

/* ── Your request: rides that match ────────────────────────────── */

function RidesForRequest({ request, rides, topDrivers, onBooked }) {
  const [open, setOpen] = useState(false);
  const [booking, setBooking] = useState('');
  const names = useNames(open ? rides.map(r => r.userID) : []);

  const book = async (ride) => {
    const n = Math.min(seatsOf(request) || 1, seatsOf(ride));
    const name = names[ride.userID] || 'the driver';
    if (!window.confirm(`Book ${n} seat${n !== 1 ? 's' : ''} with ${name}, ${when(ride)}?`)) return;
    setBooking(ride.id);
    try {
      const contact = await bookRide({ node: 'rideReserve', ride, seats: n, type: 'reservation' });
      alert(contact.number ? `Booking confirmed! Contact ${contact.name || name}: ${contact.number}` : 'Booking confirmed!');
      setOpen(false);
      onBooked?.();
    } catch (e) {
      alert(e.message);
    }
    setBooking('');
  };

  if (rides.length === 0) return null;
  return (
    <>
      <button className="match-row rides" onClick={() => setOpen(true)}>
        <span>🚗 {rides.length} ride{rides.length !== 1 ? 's' : ''} match{rides.length === 1 ? 'es' : ''} your request</span>
        <span className="match-row-cta">See {rides.length === 1 ? 'it' : 'them'} ›</span>
      </button>
      {open && (
        <Modal title={`${rides.length} ride${rides.length !== 1 ? 's match' : ' matches'} your request`}
          sub="Same route and day, leaving within 3 hours of when you asked."
          onClose={() => setOpen(false)}>
          {rides.map(r => (
            <div key={r.id} className="match-item">
              <div className="match-item-main">
                <div className="match-item-name">
                  {names[r.userID] || '…'}
                  {topDrivers[r.userID] && <span className="top-driver-star" title="Top driver" aria-label="Top driver"> ★</span>}
                </div>
                <div className="match-item-sub">
                  {displayPlace(r.fromLocation)} → {displayPlace(r.toLocation)} · {when(r)} · {seatsOf(r)} open
                  {r.donationRequested ? ` · $${String(r.donationRequested).replace(/^\$/, '')}` : ''}
                </div>
              </div>
              <button className="match-btn" disabled={booking === r.id} onClick={() => book(r)}>
                {booking === r.id ? 'Booking…' : 'Book'}
              </button>
            </div>
          ))}
        </Modal>
      )}
    </>
  );
}

/* ── People booked on your ride (or drivers who took your request) ── */

function People({ post, isRequest, bookings }) {
  const [open, setOpen] = useState(false);
  const [people, setPeople] = useState(null);
  const label = isRequest ? 'driver' : 'passenger';

  useEffect(() => {
    if (!open) return;
    let live = true;
    // A booking exists, so their contact can be shared.
    Promise.all(bookings.map(async b => ({ ...b, contact: await fetchContact(b.bookerUserID) })))
      .then(list => { if (live) setPeople(list); });
    return () => { live = false; };
  }, [open, bookings]);

  if (bookings.length === 0) return null;
  const seats = bookings.reduce((n, b) => n + (parseInt(b.seatsBooked, 10) || 1), 0);
  return (
    <>
      <button className="match-row people" onClick={() => setOpen(true)}>
        <span>{isRequest
          ? `✅ ${bookings.length} driver${bookings.length !== 1 ? 's' : ''} accepted`
          : `🧍 ${seats} seat${seats !== 1 ? 's' : ''} booked by ${bookings.length} ${label}${bookings.length !== 1 ? 's' : ''}`}</span>
        <span className="match-row-cta">See {bookings.length === 1 ? 'who' : 'all'} ›</span>
      </button>
      {open && (
        <Modal title={isRequest ? 'Drivers who accepted' : 'Passengers'} onClose={() => setOpen(false)}>
          {!people ? <p className="match-loading">Loading…</p> : people.map(p => (
            <div key={p.id} className="match-item">
              <div className="match-item-main">
                <div className="match-item-name">{p.contact.name || 'Falcon Ride user'}</div>
                <div className="match-item-sub">
                  {parseInt(p.seatsBooked, 10) || 1} seat{(parseInt(p.seatsBooked, 10) || 1) !== 1 ? 's' : ''}
                  {p.contact.number && <> · <a href={`sms:${p.contact.number}`}>{p.contact.number}</a></>}
                </div>
              </div>
              <SafetyMenu target={{
                userID: p.bookerUserID, name: p.contact.name,
                contextType: 'passenger', contextId: p.id,
              }} />
            </div>
          ))}
        </Modal>
      )}
    </>
  );
}

/**
 * Everything under one of your posts. `autoOpenMatches` opens the
 * "people need this ride" list right after you post a ride.
 */
export function PostExtras({ post, isRequest, allRides, allRequests, blocked, providerBookings, bookedRideIds, topDrivers, autoOpenMatches, onBooked }) {
  const [needOpen, setNeedOpen] = useState(!!autoOpenMatches);
  const me = getAuth().currentUser?.uid;
  const others = list => list.filter(p => p.userID !== me && !blocked.has(p.userID));
  const bookings = providerBookings.filter(b =>
    b.rideID === post.id && (b.type || 'reservation') === (isRequest ? 'request' : 'reservation'));

  return (
    <div className="match-rows">
      {isRequest
        ? <RidesForRequest request={post}
            rides={others(allRides).filter(r => !bookedRideIds.has(r.id) && requestMatchesRide(post, r))}
            topDrivers={topDrivers} onBooked={onBooked} />
        : <NeedThisRide ride={post}
            /* People already booked on this ride don't need it any more. */
            requests={others(allRequests).filter(r =>
              !bookings.some(bk => bk.bookerUserID === r.userID) && requestMatchesRide(r, post))}
            open={needOpen} setOpen={setNeedOpen} />}
      <People post={post} isRequest={isRequest} bookings={bookings} />
    </div>
  );
}
